import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import useNotification from "../../../hooks/useNotification";
import opname from "../../../services/opname";
import queryDefault from "../../../utils/queryDefault";

interface StockOpnameItem {
  id: number;
  opname_id: number;
  opname_title: string;
  branch_name: string;
  quantity: number;
  category: string;
  status: string;
  createdtime: string;
  createdby: string;
}

type ApprovalFormData = {
  status: string;
  rejection_reason?: string;
};

export default function ApprovalTab() {
  const notification = useNotification();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockOpnameItem | null>(null);
  const { control, handleSubmit, reset, watch } = useForm<ApprovalFormData>({
    defaultValues: {
      status: "accepted",
      rejection_reason: "",
    },
  });

  const selectedStatus = watch("status");

  const { data: pendingData, isLoading, refetch } = useQuery(
    ["opname-approval-list", page, rowsPerPage],
    () =>
      opname.getStockOpnameList({
        status: "pending",
        sort_by: "createdtime",
        sort_order: "asc",
        page: page + 1,
        limit: rowsPerPage,
      })
      .then(response => ({ data: response?.data, pagination: response?.pagination })),
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch pending approvals",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const approveRejectMutation = useMutation(
    (data: { id: number; payload: ApprovalFormData }) =>
      opname.updateStockOpname(data.id, data.payload),
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Stock opname approval updated successfully",
          type: "success",
          position: "top",
        });
        setOpenDialog(false);
        reset();
        setSelectedItem(null);
        refetch();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to update approval",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const items: StockOpnameItem[] = pendingData?.data || [];
  const total = pendingData?.pagination?.total || 0;

  const handleOpenDialog = (item: StockOpnameItem) => {
    setSelectedItem(item);
    reset({
      status: "accepted",
      rejection_reason: "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setSelectedItem(null);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSubmit = (data: ApprovalFormData) => {
    if (!selectedItem) return;

    if (data.status === "rejected" && !data.rejection_reason) {
      notification.onOpen({
        message: "Rejection reason is required",
        type: "error",
        position: "top",
      });
      return;
    }

    approveRejectMutation.mutate({ id: selectedItem.id, payload: data });
  };

  const handleQuickApprove = (item: StockOpnameItem) => {
    approveRejectMutation.mutate({
      id: item.id,
      payload: { status: "accepted" },
    });
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Opname</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">
                Quantity
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No pending approvals
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.branch_name}</TableCell>
                  <TableCell>{item.opname_title}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{item.category}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell>{new Date(item.createdtime).toLocaleString()}</TableCell>
                  <TableCell>{item.createdby}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => handleQuickApprove(item)}
                        disabled={approveRejectMutation.isLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Stock Opname</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Reason for Rejection"
              multiline
              rows={4}
              fullWidth
              {...control.register("rejection_reason", {
                required: "Rejection reason is required",
              })}
              placeholder="Enter the reason for rejection..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="error"
            disabled={approveRejectMutation.isLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
